import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Heart, Clock, Euro, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getTagKey = (tag) => tag.replace(/-/g, '_');

function FavoritesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`, { withCredentials: true });
      setFavorites(response.data.favorites || []);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (recipeId) => {
    try {
      await axios.delete(`${API}/favorites/${recipeId}`, { withCredentials: true });
      setFavorites(favorites.filter(f => f.recipe_id !== recipeId));
      toast.success(t('favorites.removed'));
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="py-4 sm:py-8">
      <div className="container mx-auto px-3 sm:px-4 max-w-6xl">
        <div className="flex items-center gap-3 mb-4 sm:mb-8">
          <div className="bg-red-500 rounded-full p-2 sm:p-3">
            <Heart className="w-6 h-6 sm:w-8 sm:h-8 text-white fill-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-[#2C3E2D]" data-testid="favorites-title">
            {t('favorites.title')}
          </h1>
        </div>

        {favorites.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <Heart className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold text-gray-600 mb-2">{t('favorites.empty')}</h3>
            <p className="text-sm sm:text-base text-gray-500 mb-4">{t('favorites.empty_hint')}</p>
            <Button onClick={() => navigate('/recipes')} data-testid="browse-recipes-btn">
              {t('favorites.browse_recipes')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {favorites.map((fav) => fav.recipe && (
              <Card 
                key={fav.recipe_id} 
                className="hover:shadow-lg transition-shadow cursor-pointer group relative"
                data-testid={`favorite-card-${fav.recipe_id}`}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 z-10 text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={(e) => { e.stopPropagation(); removeFavorite(fav.recipe_id); }}
                  data-testid={`remove-favorite-${fav.recipe_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                <CardContent className="pt-6" onClick={() => navigate(`/recipe/${fav.recipe_id}`)}>
                  <h3 className="text-xl font-heading font-bold mb-2 group-hover:text-primary transition-colors pr-8">
                    {fav.recipe.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{fav.recipe.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {fav.recipe.cook_time_min} {t('common.min')}
                    </div>
                    <div className="flex items-center gap-1">
                      <Euro className="w-4 h-4" />
                      €{fav.recipe.price_estimate_eur?.toFixed(2)}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-3">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="text-sm">{fav.recipe.ratings_avg?.toFixed(1) || '0.0'}</span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {fav.recipe.dietary_tags?.slice(0, 2).map((tag, idx) => (
                      <span key={idx} className="px-2 py-1 bg-success/10 text-success rounded text-xs">
                        {t(`recipes.${getTagKey(tag)}`) || tag}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default FavoritesPage;
